import React, { useState, useEffect } from "react";
import { storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faArrowRightLong,
  faXmarkLarge,
} from "@fortawesome/pro-solid-svg-icons";
import { Image, artworkInfoMap } from "./ArtInfo";

const IMAGE_FILES_2018_2024 = [
  "artwork/2020-43.webp",
  "artwork/2023-45.webp",
  "artwork/2022-44.webp",
  "artwork/2024-46.webp",
  "artwork/2018-40.webp",
  "artwork/2018-41.webp",
  "artwork/2018-42.webp",
];

const IMAGE_FILES_2017 = [
  "artwork/2017-27.webp",
  "artwork/2017-28.webp",
  "artwork/2017-29.webp",
  "artwork/2017-30.webp",
  "artwork/2017-32.webp",
  "artwork/2017-33.webp",
  "artwork/2017-38.webp",
  "artwork/2017-39.webp",
];

const IMAGE_FILES_2016 = [
  "artwork/2016-18.webp",
  "artwork/2016-19.webp",
  "artwork/2016-20.webp",
  "artwork/2016-22.webp",
  "artwork/2016-23.webp",
];

const IMAGE_FILES_2015 = [
  "artwork/2015-17.webp",
  "artwork/2015-6.webp",
  "artwork/2015-7.webp",
  "artwork/2015-8.webp",
  "artwork/2015-9.webp",
  "artwork/2015-10.webp",
  "artwork/2015-11.webp",
  "artwork/2015-12.webp",
  "artwork/2015-13.webp",
  "artwork/2015-14.webp",
  "artwork/2015-15.webp",
  "artwork/2015-16.webp",
];

const IMAGE_FILES_EARLY_WORKS = [
  "artwork/2013-1.webp",
  "artwork/2014-2.webp",
  "artwork/2014-3.webp",
  "artwork/2014-4.webp",
  "artwork/2014-5.webp",
];

const titleStyle = {
  fontFamily: '"neue-haas-grotesk-display", sans-serif',
  fontWeight: 800,
  fontStyle: "normal",
};

const ImageGallery: React.FC = () => {
  const [images2018_2024, setImages2018_2024] = useState<Image[]>([]);
  const [images2017, setImages2017] = useState<Image[]>([]);
  const [images2016, setImages2016] = useState<Image[]>([]);
  const [images2015, setImages2015] = useState<Image[]>([]);
  const [imagesEarlyWorks, setImagesEarlyWorks] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);

  useEffect(() => {
    const loadImages = async () => {
      try {
        const loadImageSet = async (files: string[], period: string) => {
          console.log(`Starting to load ${period} images...`);
          return await Promise.all(
            files.map(async (filePath, index) => {
              try {
                console.log(`Attempting to load: ${filePath}`);
                const imageRef = ref(storage, filePath);
                console.log(`Created ref for: ${filePath}`);
                const url = await getDownloadURL(imageRef);
                console.log(`Got URL for: ${filePath}`);
                const filename = filePath.split("/").pop() || "";
                const [year, number] = filename.replace(".webp", "").split("-");

                // 작품 정보 매핑
                const artworkInfo = artworkInfoMap[filename];

                return {
                  id: filename,
                  url,
                  title: artworkInfo?.title || `Work ${number}`,
                  year,
                  fileNumber: parseInt(number),
                  orderIndex: index,
                  artworkInfo,
                };
              } catch (error) {
                console.error(
                  `Error loading individual image ${filePath}:`,
                  error
                );
                throw error;
              }
            })
          );
        };

        const imagesData2018_2024 = await loadImageSet(
          IMAGE_FILES_2018_2024,
          "2018-2024"
        );

        const imagesData2017 = await loadImageSet(IMAGE_FILES_2017, "2017");
        const imagesData2016 = await loadImageSet(IMAGE_FILES_2016, "2016");
        const imagesData2015 = await loadImageSet(IMAGE_FILES_2015, "2015");
        const imagesDataEarlyWorks = await loadImageSet(
          IMAGE_FILES_EARLY_WORKS,
          "EARLY WORKS"
        );

        const sortByOrder = (a: Image, b: Image) => a.orderIndex - b.orderIndex;

        setImages2018_2024(imagesData2018_2024.sort(sortByOrder));
        setImages2017(imagesData2017.sort(sortByOrder));
        setImages2016(imagesData2016.sort(sortByOrder));
        setImages2015(imagesData2015.sort(sortByOrder));
        setImagesEarlyWorks(imagesDataEarlyWorks.sort(sortByOrder));

        console.log("All image sets loaded successfully");
        setLoading(false);
      } catch (error) {
        console.error("Error in loadImages:", error);
        if (error instanceof Error) {
          console.error("Error details:", {
            message: error.message,
            name: error.name,
            stack: error.stack,
          });
        }
        setLoading(false);
      }
    };

    loadImages();
  }, []);

  const openModal = (image: Image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const ImageStrip = ({
    images,
    galleryId,
    title,
  }: {
    images: Image[];
    galleryId: string;
    galleryType: string;
    title: string;
  }) => {
    const [scrollPosition, setScrollPosition] = useState(0);
    const [imageAspectRatios, setImageAspectRatios] = useState<{
      [key: string]: number;
    }>({});

    const handleImageLoad = (
      event: React.SyntheticEvent<HTMLImageElement>,
      imageId: string
    ) => {
      const img = event.currentTarget;
      const aspectRatio = img.naturalWidth / img.naturalHeight;
      setImageAspectRatios((prev) => ({
        ...prev,
        [imageId]: aspectRatio,
      }));
    };

    const getImageWidth = (aspectRatio: number) => {
      // Base height is 384px (h-96 = 24rem = 384px)
      // Calculate width based on aspect ratio, with minimum width of 384px
      const baseHeight = 384;
      const calculatedWidth = Math.max(baseHeight, baseHeight * aspectRatio);
      // Cap the maximum width at 1.5x the base width
      return Math.min(calculatedWidth, baseHeight * 2);
    };

    const handleScroll = (direction: "left" | "right") => {
      const container = document.getElementById(galleryId);
      if (container) {
        const scrollAmount = 300;
        const newPosition =
          direction === "left"
            ? Math.max(0, scrollPosition - scrollAmount)
            : scrollPosition + scrollAmount;

        container.scrollTo({
          left: newPosition,
          behavior: "smooth",
        });
        setScrollPosition(newPosition);
      }
    };

    return (
      <div className="mb-32 relative">
        <h1
          className="text-5xl leading-tight md:leading-none md:text-8xl ml-4 md:ml-6 font-bold  mb-4 md:mb-12 text-gray-800"
          style={titleStyle}
        >
          {title}
        </h1>
        <div className="relative w-screen">
          <div
            id={galleryId}
            className="flex overflow-x-auto overflow-y-hidden scroll-smooth hide-scrollbar pr-0"
            style={{ scrollBehavior: "smooth" }}
          >
            {images.map((image) => (
              <div
                key={image.id}
                className="flex-none w-96 h-96 px-3"
                style={{
                  width: imageAspectRatios[image.id]
                    ? `${getImageWidth(imageAspectRatios[image.id])}px`
                    : "384px",
                }}
                onClick={() => openModal(image)}
              >
                <div className="w-full h-full relative cursor-pointer group">
                  <img
                    src={image.url}
                    alt={image.title}
                    onLoad={(e) => handleImageLoad(e, image.id)}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => handleScroll("left")}
          className="absolute ml-10 mt-10 left-4 transform -translate-y-1/2 z-10 text-gray-600"
        >
          <FontAwesomeIcon icon={faArrowLeftLong} className="w-6 h-6" />
        </button>

        <button
          onClick={() => handleScroll("right")}
          className="absolute mr-10 mt-10 right-4 transform -translate-y-1/2 z-10 text-gray-600"
        >
          <FontAwesomeIcon icon={faArrowRightLong} className="w-6 h-6" />
        </button>
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="relative w-full">
      <div className="w-full">
        <ImageStrip
          images={images2018_2024}
          galleryId="gallery-2018-2024"
          galleryType="2018-2024"
          title="2018-2024"
        />
        <ImageStrip
          images={images2017}
          galleryId="gallery-2017"
          galleryType="2017"
          title="2017"
        />
        <ImageStrip
          images={images2016}
          galleryId="gallery-2016"
          galleryType="2016"
          title="2016"
        />
        <ImageStrip
          images={images2015}
          galleryId="gallery-2015"
          galleryType="2015"
          title="2015"
        />
        <ImageStrip
          images={imagesEarlyWorks}
          galleryId="gallery-Early-Works"
          galleryType="EARLY WORKS"
          title="EARLY WORKS"
        />
      </div>

      <Transition show={!!selectedImage} as={Fragment}>
        <Dialog onClose={closeModal} className="fixed inset-0 z-50">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-white/50 backdrop-blur-md" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="fixed inset-0 flex items-center justify-center p-4">
              <button
                onClick={closeModal}
                className="absolute top-8 right-8 text-white z-10"
              >
                <FontAwesomeIcon
                  icon={faXmarkLarge}
                  className="w-6 h-6 text-gray-800"
                />
              </button>

              {selectedImage && (
                <div className="relative max-w-9xl group py-12 flex justify-center">
                  {" "}
                  {/* flex와 justify-center 추가 */}
                  <div className="relative">
                    {" "}
                    {/* 새로운 wrapper div 추가 */}
                    <img
                      src={selectedImage.url}
                      alt={selectedImage.title}
                      className="w-full h-auto object-contain max-h-[90vh]"
                    />
                    {/* 정보 표시 영역 */}
                    <div
                      className="absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out"
                      style={{ pointerEvents: "none" }}
                    >
                      <div className="bg-black/80 p-6">
                        <h3
                          className="text-2xl font-bold text-white"
                          style={{
                            fontFamily:
                              '"neue-haas-grotesk-display", sans-serif',
                            fontWeight: 600,
                            fontStyle: "normal",
                          }}
                        >
                          {selectedImage.artworkInfo?.title ||
                            selectedImage.title}
                        </h3>
                        {selectedImage.artworkInfo && (
                          <div
                            className="space-y-0 mt-3 text-gray-400"
                            style={{
                              fontFamily:
                                '"neue-haas-grotesk-display", sans-serif',
                              fontWeight: 600,
                              fontStyle: "normal",
                            }}
                          >
                            <p className="text-sm">
                              {selectedImage.artworkInfo.type}
                            </p>
                            <p className="text-sm">
                              {selectedImage.artworkInfo.size}
                            </p>
                            <p className="text-sm">
                              {selectedImage.artworkInfo.year}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
    </div>
  );
};

export default ImageGallery;
